import { api } from '@/lib/api';

/**
 * Tipos do módulo agent-analytics — espelho do `src/modules/agent-analytics/types.ts`
 * do backend. Redefinidos localmente porque não vão pro `@troia-v3/types`
 * (são shapes internos do TroiaChat derivados de dados do Langfuse).
 */

export type AnalyticsPeriod = '1d' | '7d' | '30d' | '90d';

/**
 * Intervalo "Personalizado" do seletor de período — instantes ISO 8601 do
 * início do primeiro dia ao fim do último, no fuso do navegador
 * (`customRangeToIso`). Quando vai junto, o backend usa ele no lugar da
 * janela do `period`.
 */
export interface AnalyticsDateRange {
  startDate: string;
  endDate: string;
}

export interface AgentMetricsResponse {
  agentId: string;
  agentName: string;
  period: AnalyticsPeriod;
  totalExecutions: number;
  totalCostUsd: number;
  avgLatencySeconds: number;
  p95LatencySeconds: number;
  executionsWithTools: number;
  distinctSessions: number;
  from: string;
  to: string;
}

export interface AgentTraceSummary {
  traceId: string;
  timestamp: string;
  sessionId: string;
  outputSnippet: string;
  model: string | null;
  averageScore: number | null;
  costUsd: number;
  latencySeconds: number;
  tags: string[];
}

export interface AgentTracesListResponse {
  items: AgentTraceSummary[];
  total: number;
  page: number;
  limit: number;
}

export interface AgentTraceDetailResponse {
  traceId: string;
  timestamp: string;
  sessionId: string;
  name: string;
  input: unknown;
  output: unknown;
  tags: string[];
  metadata: Record<string, unknown> | null;
  costUsd: number;
  latencySeconds: number;
  scores: Array<{
    name: string;
    value: number;
    comment: string | null;
    source: string;
  }>;
  observations: Array<{
    id: string;
    type: string;
    name: string;
    startTime: string;
    endTime: string | null;
    model: string | null;
    inputTokens: number | null;
    outputTokens: number | null;
    costUsd: number | null;
    latencySeconds: number | null;
  }>;
  langfusePath: string;
}

export interface AgentScoresResponse {
  agentId: string;
  period: AnalyticsPeriod;
  byMetric: Record<
    string,
    {
      average: number;
      count: number;
      min: number;
      max: number;
    }
  >;
  totalScores: number;
}

export interface AgentCostBreakdownResponse {
  agentId: string;
  period: AnalyticsPeriod;
  totalUsd: number;
  totalCredits: number;
  totalInputCredits: number;
  totalOutputCredits: number;
  byDay: Array<{
    date: string;
    costUsd: number;
    executions: number;
  }>;
  byModel: Array<{
    model: string;
    costUsd: number;
    executions: number;
    inputCredits: number;
    outputCredits: number;
  }>;
}

export interface KnowledgeGapPattern {
  /** Chave estável do padrão, usada como ID pra marcar como resolvido */
  gapKey: string;
  /** Padrão regex — mantido por retrocompat (== gapKey) */
  pattern: string;
  label: string;
  count: number;
  severity: 'low' | 'medium' | 'high';
  samples: Array<{
    traceId: string;
    timestamp: string;
    sessionId: string;
    /** Trecho do output do agente (até 200 chars). */
    snippet: string;
    /**
     * Mensagem mais recente do cliente que originou a resposta do agente,
     * truncada a 200 chars. Adicionado em 2026-05-13 pra contextualizar
     * o gap na UI.
     */
    customerMessage?: string;
  }>;
  /** true se o cliente marcou como resolvido E não há samples novos */
  isResolved: boolean;
  /**
   * true quando o gap foi resolvido no passado mas há samples novos
   * depois da `resolvedAt` — significa que a solução não funcionou.
   * UI deve mostrar destaque amarelo de "reapareceu".
   */
  previouslyResolved: boolean;
  /** Quantos samples ocorreram após a resolução (relevante quando previouslyResolved=true) */
  samplesAfterResolution: number;
  /** Metadata da resolução (presente quando há registro de resolve) */
  resolution?: {
    resolvedAt: string;
    resolvedBy: string;
    databaseDocumentId?: string;
    databaseId?: string;
    note?: string;
    /** true quando o documento referenciado foi deletado (watchdog) */
    databaseDocumentMissing?: boolean;
  };
}

export interface AgentKnowledgeGapsResponse {
  agentId: string;
  period: AnalyticsPeriod;
  patterns: KnowledgeGapPattern[];
  totalOccurrences: number;
  /** Quantos gaps detectados no período já estão resolvidos */
  resolvedCount: number;
}

/** Categorias de documento aceitas pelo backend (DocumentCategory enum) */
export type DocumentCategoryValue =
  | 'article'
  | 'manual'
  | 'faq'
  | 'policy'
  | 'guide'
  | 'tutorial'
  | 'report'
  | 'whitepaper'
  | 'case_study'
  | 'other';

export interface ResolveGapRequest {
  /** Se criar document na base: ID da database do tipo 'documents' */
  databaseId?: string;
  /** Se criar document: dados do document */
  documentData?: {
    title: string;
    content: string;
    /** Default 'faq' se não fornecido */
    category?: DocumentCategoryValue;
    keywords?: string[];
  };
  /** Nota livre opcional */
  note?: string;
  /**
   * Quando true, substitui qualquer resolução existente (usado quando o
   * gap reapareceu e cliente quer responder novamente).
   */
  replaceExisting?: boolean;
}

export interface ResolveGapResponse {
  id: string;
  gapKey: string;
  databaseDocumentId?: string;
  databaseId?: string;
  note?: string;
  resolvedAt: string;
  resolvedBy: string;
}

export interface AgentHealthScoreResponse {
  agentId: string;
  period: AnalyticsPeriod;
  overallScore: number;
  components: {
    relevance?: number;
    hallucination?: number;
    tone?: number;
    toolUsage?: number;
  };
  traceCount: number;
  hasEvaluators: boolean;
}

// ─── Health trends (série temporal) ──────────────────────────────────────

export interface HealthTrendPoint {
  date: string;
  overallScore: number | null;
  traceCount: number;
}

export interface AgentHealthTrendsResponse {
  agentId: string;
  period: AnalyticsPeriod;
  points: HealthTrendPoint[];
  hasEvaluators: boolean;
}

// ─── Insights narrativos (LLM) ───────────────────────────────────────────

export type InsightCategory = 'positive' | 'improvement' | 'insight';

export interface AgentInsight {
  category: InsightCategory;
  title: string;
  description: string;
  relatedMetrics?: string[];
}

export type InsightFallbackReason =
  | 'no_openai'
  | 'llm_error'
  | 'no_data'
  | 'parse_error';

export interface AgentInsightsResponse {
  agentId: string;
  period: AnalyticsPeriod;
  positive: AgentInsight[];
  improvements: AgentInsight[];
  insights: AgentInsight[];
  hasNarrative: boolean;
  fallbackReason?: InsightFallbackReason;
  generatedAt: string;
}

// ─── API service ─────────────────────────────────────────────────────────

/** `startDate`/`endDate` na query só quando há intervalo personalizado. */
const rangeParams = (range?: AnalyticsDateRange): Partial<AnalyticsDateRange> =>
  range ? { startDate: range.startDate, endDate: range.endDate } : {};

export const agentAnalyticsApi = {
  async getMetrics(
    agentId: string,
    period: AnalyticsPeriod = '7d',
    range?: AnalyticsDateRange,
  ): Promise<AgentMetricsResponse> {
    const response = await api.get(`/agent-analytics/${agentId}/metrics`, {
      params: { period, ...rangeParams(range) },
    });
    return response.data.data;
  },

  async listTraces(
    agentId: string,
    options: {
      period?: AnalyticsPeriod;
      limit?: number;
      page?: number;
      range?: AnalyticsDateRange;
    } = {},
  ): Promise<AgentTracesListResponse> {
    const response = await api.get(`/agent-analytics/${agentId}/traces`, {
      params: {
        period: options.period ?? '7d',
        limit: options.limit,
        page: options.page,
        ...rangeParams(options.range),
      },
    });
    return response.data.data;
  },

  async getTraceDetail(
    agentId: string,
    traceId: string,
  ): Promise<AgentTraceDetailResponse> {
    const response = await api.get(
      `/agent-analytics/${agentId}/traces/${traceId}`,
    );
    return response.data.data;
  },

  async listScores(
    agentId: string,
    period: AnalyticsPeriod = '7d',
    range?: AnalyticsDateRange,
  ): Promise<AgentScoresResponse> {
    const response = await api.get(`/agent-analytics/${agentId}/scores`, {
      params: { period, ...rangeParams(range) },
    });
    return response.data.data;
  },

  async getCosts(
    agentId: string,
    period: AnalyticsPeriod = '30d',
    range?: AnalyticsDateRange,
  ): Promise<AgentCostBreakdownResponse> {
    const response = await api.get(`/agent-analytics/${agentId}/costs`, {
      params: { period, ...rangeParams(range) },
    });
    return response.data.data;
  },

  async getKnowledgeGaps(
    agentId: string,
    period: AnalyticsPeriod = '7d',
    includeResolved = false,
    range?: AnalyticsDateRange,
  ): Promise<AgentKnowledgeGapsResponse> {
    const response = await api.get(
      `/agent-analytics/${agentId}/knowledge-gaps`,
      {
        params: {
          period,
          ...rangeParams(range),
          ...(includeResolved ? { includeResolved: 'true' } : {}),
        },
      },
    );
    return response.data.data;
  },

  /**
   * Gera um rascunho (título + content) de doc-KB para resolver um gap.
   * Backend chama LLM com as samples do gap (cliente + agente) e devolve
   * sugestão semântica. Substitui o template genérico `buildSuggestedContent`.
   */
  async generateGapDraft(
    agentId: string,
    input: {
      gapLabel: string;
      samples: Array<{ customerMessage?: string; agentSnippet: string }>;
    },
  ): Promise<{ title: string; content: string }> {
    const response = await api.post(
      `/agent-analytics/${agentId}/knowledge-gaps/draft`,
      input,
    );
    return response.data.data;
  },

  async resolveKnowledgeGap(
    agentId: string,
    gapKey: string,
    input: ResolveGapRequest,
  ): Promise<ResolveGapResponse> {
    const response = await api.post(
      `/agent-analytics/${agentId}/knowledge-gaps/${encodeURIComponent(gapKey)}/resolve`,
      input,
    );
    return response.data.data;
  },

  async unresolveKnowledgeGap(agentId: string, gapKey: string): Promise<void> {
    await api.delete(
      `/agent-analytics/${agentId}/knowledge-gaps/${encodeURIComponent(gapKey)}/resolve`,
    );
  },

  async getHealthScore(
    agentId: string,
    period: AnalyticsPeriod = '7d',
    range?: AnalyticsDateRange,
  ): Promise<AgentHealthScoreResponse> {
    const response = await api.get(
      `/agent-analytics/${agentId}/health-score`,
      { params: { period, ...rangeParams(range) } },
    );
    return response.data.data;
  },

  async getHealthTrends(
    agentId: string,
    period: AnalyticsPeriod = '30d',
    range?: AnalyticsDateRange,
  ): Promise<AgentHealthTrendsResponse> {
    const response = await api.get(
      `/agent-analytics/${agentId}/health-trends`,
      { params: { period, ...rangeParams(range) } },
    );
    return response.data.data;
  },

  async getInsights(
    agentId: string,
    period: AnalyticsPeriod = '7d',
    range?: AnalyticsDateRange,
  ): Promise<AgentInsightsResponse> {
    const response = await api.get(
      `/agent-analytics/${agentId}/insights`,
      { params: { period, ...rangeParams(range) } },
    );
    return response.data.data;
  },
};
